import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { array, string, bool, number, func, node } from 'prop-types';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getExpandedRowModel,
  flexRender,
} from '@tanstack/react-table';
import { GLOBAL } from '@Config/global';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './styles.scss';
import ConfirmationModal from '@Components/ConfirmationModal';
import CellInput from './components/CellInput';
import { ResizeHandle } from './components';
import Tooltip from '@Components/Tooltip';

const renderSpan = (spanClass) => (
  <span className={spanClass} />
);

const TextProps = {
  previousText: renderSpan('before-btn'),
  nextText: renderSpan('after-btn'),
  ofText: GLOBAL.TABLE_VIEW.OF_TEXT,
  rowsText: GLOBAL.TABLE_VIEW.ROWS_TEXT,
  pageText: GLOBAL.TABLE_VIEW.PAGE_TEXT
};

const normalizeSorting = (sorting) => (Array.isArray(sorting) ? sorting : []);

const normalizeColumnFilters = (filters) => (Array.isArray(filters) ? filters : []);

const getColumnFilterValues = (filters) => normalizeColumnFilters(filters)
  .reduce((values, { id, value }) => ({ ...values, [id]: value ?? '' }), {});

const getColumnFilters = (values) => Object.entries(values)
  .filter(([_, value]) => typeof value === 'string' && value.trim() !== '')
  .map(([id, value]) => ({ id, value: value.trim().toLowerCase() }));

const areFilterValuesEqual = (left, right) => {
  const leftEntries = Object.entries(left);
  const rightEntries = Object.entries(right);

  return leftEntries.length === rightEntries.length
    && leftEntries.every(([id, value]) => right[id] === value);
};

const Table = ({
  navigate,
  columns,
  className = "",
  data,
  defaultPageSize = 10,
  filterable = true,
  onSortedChange,
  onPageSizeChange,
  changePage,
  loading = false,
  loadingContent = null,
  filter,
  sorted = [],
  manual = false,
  onKeyDown,
  pages,
  page,
  onFilterChange,
  pageSize = 10,
  expandable = false,
  subComponent = null,
  resetColumnFilters = false,
  isChildTable = false,
  hidePagination = false,
  hideFooter = false,
  getRowId,
  getRowProps: customGetRowProps }) => {
  const [selection, setSelection] = useState([]);
  const [selectAll] = useState(false);
  const [editingRow] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [internalPageSize, setInternalPageSize] = useState(pageSize || defaultPageSize || 10);
  const [internalPageIndex, setInternalPageIndex] = useState(page || 0);
  const [internalSorting, setInternalSorting] = useState(() => normalizeSorting(sorted));
  const [internalColumnFilters, setInternalColumnFilters] = useState(() => normalizeColumnFilters(filter));
  const [columnFilterValues, setColumnFilterValues] = useState(() => getColumnFilterValues(filter));
  const [columnWidths, setColumnWidths] = useState({});
  const [resizingColumn, setResizingColumn] = useState(null);
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);
  const [isCompactLayout, setIsCompactLayout] = useState(false);

  const filterTimeoutRef = useRef(null);
  const lastSubmittedFilterValuesRef = useRef(getColumnFilterValues(filter));
  const refDivTable = useRef(null);
  const refDivScroll1 = useRef(null);
  const refDivScroll2 = useRef(null);
  const syncingScrollRef = useRef(false);

  useEffect(() => {
    const initialWidths = {};

    columns.forEach((col) => {
      if (!col.width) {
        return;
      }

      const columnId = col.id || col.accessor || col.field || col.name;
      if (columnId) {
        initialWidths[columnId] = col.width;
      }
    });

    if (Object.keys(initialWidths).length > 0) {
      setColumnWidths(initialWidths);
    }
  }, [columns]);

  const toggleDeleteModal = () => {
    setShowDeleteModal(!showDeleteModal);
  };

  const toggleExportModal = () => {
    setShowExportModal(!showExportModal);
  };

  const onSuccessRedirect = () => {
    navigate('/table-success');
  };

  const toggleAllSelection = () => {
    if (selectAll) {
      setSelection(data.map((record) => `select-${record.id}`));
    } else {
      setSelection([]);
    }
  };

  const isSelected = (key) => selection.includes(`select-${key}`);

  const isEditing = (key) => editingRow && editingRow.index === key;

  const getSelectedRowProps = (rowInfo) => (rowInfo && isSelected(rowInfo.original.id))
    ? "-selected"
    : (rowInfo && selection.length && !isSelected(rowInfo.original.id) ? '-disabled' : '');

  const getEditingRowProps = (rowInfo) => rowInfo && isEditing(rowInfo.index)
    ? "-editing"
    : (rowInfo && editingRow && !isEditing(rowInfo.index) ? '-disabled' : '');

  useEffect(() => {
    toggleAllSelection();
  }, [selectAll]);

  const memoizedColumns = useMemo(() => {
    let processedColumns = columns.map((col) => {
      let accessorKey;
      let columnId;

      if (typeof col.id === 'string') {
        accessorKey = col.id;
        columnId = col.id;
      } else if (typeof col.accessor === 'string') {
        accessorKey = col.accessor;
        columnId = col.accessor;
      } else if (typeof col.field === 'string') {
        accessorKey = col.field;
        columnId = col.field;
      } else if (typeof col.name === 'string') {
        accessorKey = col.name;
        columnId = col.name;
      }

      return {
        ...col,
        id: columnId || accessorKey,
        header: col.Header,
        accessorKey,
        cell: col.Cell
          ? (props) => col.Cell({ ...props, value: props.getValue(), original: props.row.original })
          : (props) => props.getValue(),
        footer: col.Footer ? (context) => {
          const rowData = table.getRowModel().rows.map((row) => row.original);
          return col.Footer({ ...context, data: rowData });
        } : undefined,
        enableSorting: col.sortable !== false,
        enableColumnFilter: col.filterable !== false,
        headerStyle: col.headerStyle || {},
        style: col.style || {},
      };
    }).filter((col) => col.accessorKey);

    if (expandable) {
      const expanderColumn = {
        id: 'expander',
        header: () => null,
        accessorKey: 'expander',
        width: 35,
        enableSorting: false,
        enableColumnFilter: false,
        headerStyle: { width: '35px', minWidth: '35px', maxWidth: '35px', padding: '0' },
        style: { width: '35px', minWidth: '35px', maxWidth: '35px', padding: '0', textAlign: 'center' },
        cell: ({ row }) => (
          <Tooltip
            text={row.getIsExpanded() ? 'Contraer fila' : 'Expandir fila'}
            placement="right"
          >
            <button
              type="button"
              className={`table-expander-button ${row.getIsExpanded() ? 'is-expanded' : ''}`}
              onClick={() => row.toggleExpanded()}
              aria-label={row.getIsExpanded() ? 'Contraer fila' : 'Expandir fila'}
              aria-expanded={row.getIsExpanded()}
            >
              <i className="bi bi-chevron-right" aria-hidden="true" />
            </button>
          </Tooltip>
        ),
      };

      processedColumns = [expanderColumn, ...processedColumns];
    }

    return processedColumns;
  }, [columns, expandable, data]);

  const controlledSorting = useMemo(() => normalizeSorting(sorted), [sorted]);
  const controlledColumnFilters = useMemo(() => normalizeColumnFilters(filter), [filter]);
  const controlledFilterValues = useMemo(
    () => getColumnFilterValues(controlledColumnFilters),
    [controlledColumnFilters]
  );
  const activeSorting = manual ? controlledSorting : internalSorting;
  const activeColumnFilters = manual ? controlledColumnFilters : internalColumnFilters;

  const table = useReactTable({
    data: data || [],
    columns: memoizedColumns,
    getRowId,
    state: {
      sorting: activeSorting,
      pagination: {
        pageIndex: manual ? page : internalPageIndex,
        pageSize: manual ? pageSize : internalPageSize,
      },
      columnFilters: activeColumnFilters,
    },
    getExpandedRowModel: getExpandedRowModel(),
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(activeSorting) : updater;
      if (manual && onSortedChange) {
        onSortedChange(newSorting);
      }
      else {
        setInternalSorting(newSorting);
      }
    },
    onPaginationChange: (updater) => {
      const currentState = manual ? {
        pageIndex: page || 0,
        pageSize: pageSize || defaultPageSize || 10,
      } : {
        pageIndex: internalPageIndex,
        pageSize: internalPageSize,
      };

      const newState = typeof updater === 'function' ? updater(currentState) : updater;

      if (manual && changePage) {
        changePage(newState.pageIndex);
      } else {
        setInternalPageIndex(newState.pageIndex);
        setInternalPageSize(newState.pageSize);
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: manual,
    manualSorting: manual,
    manualFiltering: manual,
    pageCount: manual ? pages : undefined,
  });

  const calculateTopDivScroll = useCallback(() => {
    if (isChildTable) {
      return 1000;
    }

    return Math.max(window.innerHeight - 20, 0);
  }, [isChildTable]);

  const updateScrollMetrics = useCallback(() => {
    const tableViewport = refDivTable.current;

    if (!tableViewport) {
      return;
    }

    const scrollWidth = tableViewport.scrollWidth || window.innerWidth;
    const clientWidth = tableViewport.clientWidth || window.innerWidth;
    const nextHasHorizontalOverflow = scrollWidth - clientWidth > 1;
    const nextIsCompactLayout = !isChildTable && clientWidth <= 960;

    setHasHorizontalOverflow((prev) => (prev === nextHasHorizontalOverflow ? prev : nextHasHorizontalOverflow));
    setIsCompactLayout((prev) => (prev === nextIsCompactLayout ? prev : nextIsCompactLayout));

    if (refDivScroll2.current) {
      refDivScroll2.current.style.width = `${scrollWidth}px`;
    }

    if (refDivScroll1.current) {
      refDivScroll1.current.style.top = `${calculateTopDivScroll()}px`;
    }
  }, [calculateTopDivScroll, isChildTable]);

  useEffect(() => {
    updateScrollMetrics();
  }, [updateScrollMetrics, data, memoizedColumns, columnWidths, filterable, hideFooter, hidePagination]);

  useEffect(() => {
    const handleResize = () => {
      updateScrollMetrics();
    };

    window.addEventListener('resize', handleResize);

    let resizeObserver;

    if (typeof ResizeObserver !== 'undefined' && refDivTable.current) {
      resizeObserver = new ResizeObserver(() => {
        updateScrollMetrics();
      });

      resizeObserver.observe(refDivTable.current);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [updateScrollMetrics]);

  const syncScrollPosition = useCallback((sourceRef, targetRef) => {
    if (!sourceRef.current || !targetRef.current || syncingScrollRef.current) {
      return;
    }

    syncingScrollRef.current = true;
    targetRef.current.scrollLeft = sourceRef.current.scrollLeft;

    window.requestAnimationFrame(() => {
      syncingScrollRef.current = false;
    });
  }, []);

  const scrollHorizontalTable = useCallback(() => {
    updateScrollMetrics();
    syncScrollPosition(refDivScroll1, refDivTable);
  }, [syncScrollPosition, updateScrollMetrics]);

  const syncStickyScrollbar = useCallback(() => {
    syncScrollPosition(refDivTable, refDivScroll1);
  }, [syncScrollPosition]);

  const buildRowProps = (row) => {
    const rowInfo = { original: row.original, index: row.index };
    const customProps = customGetRowProps ? customGetRowProps(rowInfo) || {} : {};
    const baseClassName = `${getSelectedRowProps(rowInfo)} ${getEditingRowProps(rowInfo)}`.trim();
    const customClassName = customProps.className || '';

    return {
      ...customProps,
      className: `${baseClassName} ${customClassName}`.trim(),
      style: {
        ...(customProps.style || {})
      }
    };
  };

  const clearPendingColumnFilterChange = useCallback(() => {
    if (filterTimeoutRef.current) {
      clearTimeout(filterTimeoutRef.current);
      filterTimeoutRef.current = null;
    }
  }, []);

  const applyColumnFilters = useCallback((nextFilterValues) => {
    const nextColumnFilters = getColumnFilters(nextFilterValues);

    if (manual) {
      lastSubmittedFilterValuesRef.current = nextFilterValues;
      onFilterChange?.(nextColumnFilters);
      return;
    }

    setInternalColumnFilters(nextColumnFilters);
  }, [manual, onFilterChange]);

  const handleColumnFilterChange = useCallback((columnId, value) => {
    setColumnFilterValues((currentValues) => {
      const nextFilterValues = { ...currentValues, [columnId]: value };

      clearPendingColumnFilterChange();
      filterTimeoutRef.current = setTimeout(() => {
        applyColumnFilters(nextFilterValues);
        filterTimeoutRef.current = null;
      }, 500);

      return nextFilterValues;
    });
  }, [applyColumnFilters, clearPendingColumnFilterChange]);

  useEffect(() => {
    if (!manual || areFilterValuesEqual(controlledFilterValues, lastSubmittedFilterValuesRef.current)) {
      return;
    }

    clearPendingColumnFilterChange();
    setColumnFilterValues(controlledFilterValues);
    lastSubmittedFilterValuesRef.current = controlledFilterValues;
  }, [clearPendingColumnFilterChange, controlledFilterValues, manual]);

  useEffect(() => {
    if (!resetColumnFilters) {
      return;
    }

    clearPendingColumnFilterChange();
    setColumnFilterValues({});
    setInternalColumnFilters([]);
    lastSubmittedFilterValuesRef.current = {};
  }, [clearPendingColumnFilterChange, resetColumnFilters]);

  useEffect(() => () => {
    clearPendingColumnFilterChange();
  }, [clearPendingColumnFilterChange]);

  const handleColumnResize = useCallback((columnId, width) => {
    setColumnWidths((prev) => ({
      ...prev,
      [columnId]: width
    }));
  }, []);

  const handleColumnResizeStart = useCallback((columnId) => {
    setResizingColumn(columnId);
  }, []);

  const handleColumnResizeEnd = useCallback(() => {
    setResizingColumn(null);
  }, []);

  const getColumnWidth = useCallback((columnId) => {
    if (columnId === 'expander' && expandable) {
      return '35px';
    }

    return columnWidths[columnId] || null;
  }, [columnWidths, expandable]);

  const shouldShowHorizontalScrollbar = !isChildTable && hasHorizontalOverflow && !isCompactLayout;
  const skeletonRowCount = Math.min(Math.max(pageSize || defaultPageSize, 4), 8);
  const tableClassName = [
    'react-table',
    '-striped',
    '-highlight',
    className,
    resizingColumn ? 'table-resizing' : '',
    isChildTable ? 'is-child-table' : '',
    !isChildTable && isCompactLayout ? 'is-compact' : ''
  ].filter(Boolean).join(' ');

  return (
    <React.Fragment>
      {showDeleteModal && (
        <ConfirmationModal
          message={GLOBAL.TABLE_VIEW.DELETE_MODAL_BODY}
          status="warning"
          title={GLOBAL.TABLE_VIEW.DELETE_MODAL_TITLE}
          onDismiss={toggleDeleteModal}
          onConfirm={onSuccessRedirect}
        />
      )}
      {showExportModal && (
        <ConfirmationModal
          message={GLOBAL.TABLE_VIEW.EXPORT_MODAL_BODY}
          title={GLOBAL.TABLE_VIEW.EXPORT_MODAL_TITLE}
          status="primary"
          onDismiss={toggleExportModal}
          onConfirm={onSuccessRedirect}
        />
      )}

      {shouldShowHorizontalScrollbar && (
        <div
          ref={refDivScroll1}
          onScroll={scrollHorizontalTable}
          className={`table-horizontal-scroll ${isChildTable ? 'is-child-scroll' : 'is-parent-scroll'}`}
          style={{
            overflow: "auto",
            zIndex: "2",
            position: isChildTable ? "relative" : "sticky",
            top: isChildTable ? "0" : `${calculateTopDivScroll()}px`,
            marginBottom: isChildTable ? "8px" : "0"
          }}
        >
          <div
            ref={refDivScroll2}
            style={{
              width: `${refDivTable.current?.scrollWidth || window.innerWidth + 10}px`,
              height: "1px"
            }}
          />
        </div>
      )}

      <div
        ref={refDivTable}
        onScroll={syncStickyScrollbar}
        className="react-table-wrapper"
        style={{
          overflowX: isChildTable ? "visible" : "auto",
          overflowY: isChildTable ? "visible" : "hidden",
          marginBottom: isChildTable ? "0" : "20px",
          width: isChildTable ? "max-content" : "100%",
          minWidth: "100%",
          maxWidth: isChildTable ? "none" : "100%",
          height: "100%",
          position: "relative"
        }}
      >
        <div className={tableClassName}>
          <table aria-busy={loading}>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <React.Fragment key={headerGroup.id}>
                  <tr>
                    {headerGroup.headers.map((header) => (
                      <Tooltip
                        key={header.id}
                        text={header.column.columnDef.description || (typeof header.column.columnDef.header === 'function' ? '' : header.column.columnDef.header)}
                        placement="top"
                        disabled={!header.column.columnDef.description && (!header.column.columnDef.header || typeof header.column.columnDef.header === 'function')}
                      >
                        <th
                          style={{
                            ...header.column.columnDef.headerStyle,
                            cursor: 'default',
                            width: getColumnWidth(header.id),
                            minWidth: getColumnWidth(header.id),
                            maxWidth: getColumnWidth(header.id),
                            position: 'relative',
                            zIndex: header.index + 1
                          }}
                          className={`${header.column.getCanSort() ? `${header.column.getIsSorted() === 'asc' ? '-sort-asc' : header.column.getIsSorted() === 'desc' ? '-sort-desc' : ''}` : ''} ${header.id !== 'expander' ? 'resizable' : ''} ${header.id === 'expander' ? 'expander-column' : ''}`}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              width: '100%',
                              paddingRight: header.column.getCanSort() ? '25px' : '8px'
                            }}
                          >
                            <span>
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                            </span>
                            {header.column.getCanSort() && (
                              <button
                                type="button"
                                className={`table-sort-button ${header.column.getIsSorted() ? 'is-active' : ''}`}
                                onClick={header.column.getToggleSortingHandler()}
                                aria-label={`Ordenar por ${typeof header.column.columnDef.header === 'string' ? header.column.columnDef.header : 'columna'}`}
                              >
                                <i className={`bi ${header.column.getIsSorted() === 'asc' ? 'bi-arrow-down' : header.column.getIsSorted() === 'desc' ? 'bi-arrow-up' : 'bi-arrow-down-up'}`} aria-hidden="true" />
                              </button>
                            )}
                          </div>
                          {header.id !== 'expander' && (
                            <ResizeHandle
                              onResizeStart={() => handleColumnResizeStart(header.id)}
                              onResize={(width) => handleColumnResize(header.id, width)}
                              onResizeEnd={handleColumnResizeEnd}
                              minWidth={50}
                              maxWidth={500}
                            />
                          )}
                        </th>
                      </Tooltip>
                    ))}
                  </tr>
                  {filterable && (
                    <tr key={`${headerGroup.id}-filter-row`} className="table-filter-row">
                      {headerGroup.headers.map((header) => (
                        <th
                          key={`${header.id}-filter`}
                          style={{
                            ...header.column.columnDef.headerStyle,
                            padding: '8px 10px',
                            backgroundColor: '#f8f9fa',
                            borderBottom: '1px solid rgba(0,0,0,0.05)',
                            width: getColumnWidth(header.id),
                            minWidth: getColumnWidth(header.id),
                            maxWidth: getColumnWidth(header.id),
                            zIndex: header.index + 1
                          }}
                        >
                          {header.column.columnDef.enableColumnFilter !== false && (
                            <CellInput
                              value={columnFilterValues[header.id] || ''}
                              onChange={(e) => handleColumnFilterChange(header.id, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                        </th>
                      ))}
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </thead>
            <tbody>
              {loading ? loadingContent ? (
                <tr><td colSpan={memoizedColumns.length}>{loadingContent}</td></tr>
              ) : Array.from({ length: skeletonRowCount }, (_, rowIndex) => (
                <tr key={`skeleton-${rowIndex}`} className="table-skeleton-row">
                  {memoizedColumns.map((column) => (
                    <td
                      key={`skeleton-${rowIndex}-${column.id}`}
                      style={{
                        ...column.style,
                        width: getColumnWidth(column.id),
                        minWidth: getColumnWidth(column.id),
                        maxWidth: getColumnWidth(column.id)
                      }}
                    >
                      <span className="table-skeleton" aria-hidden="true" />
                    </td>
                  ))}
                </tr>
              )) : table.getRowModel().rows.map((row) => (
                <React.Fragment key={row.id}>
                  <tr {...buildRowProps(row)}>
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        style={{
                          ...cell.column.columnDef.style,
                          width: getColumnWidth(cell.column.id),
                          minWidth: getColumnWidth(cell.column.id),
                          maxWidth: getColumnWidth(cell.column.id),
                          zIndex: (cell.column.index || 0) + 1
                        }}
                        className={cell.column.id === 'expander' ? 'expander-column' : ''}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                  {expandable && subComponent && row.getIsExpanded() && (
                    <tr className="table-expanded-row">
                      <td colSpan={memoizedColumns.length} className="table-expanded-cell">
                        {subComponent({ original: row.original })}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
            {!hideFooter && (
              <tfoot>
                {table.getFooterGroups().map((footerGroup) => (
                  <tr key={footerGroup.id}>
                    {footerGroup.headers.map((header) => (
                      <td
                        key={header.id}
                        style={{
                          ...header.column.columnDef.style,
                          width: getColumnWidth(header.id),
                          minWidth: getColumnWidth(header.id),
                          maxWidth: getColumnWidth(header.id),
                          fontWeight: 'bold',
                          backgroundColor: '#f8f9fa',
                          borderTop: '2px solid #dee2e6'
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.footer,
                            header.getContext()
                          )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tfoot>
            )}
          </table>

          {!hidePagination && (
            <div className="pagination-bottom">
              <div className="-pagination">
                <div className="-previous">
                  <Tooltip
                    text="Página anterior"
                    placement="top"
                    disabled={!table.getCanPreviousPage()}
                  >
                    <button
                      className="-btn"
                      onClick={() => table.previousPage()}
                      disabled={!table.getCanPreviousPage()}
                    >
                      {TextProps.previousText}
                    </button>
                  </Tooltip>
                </div>
                <div className="-center">
                  <span className="-pageInfo">
                    {TextProps.pageText}{' '}
                    <strong>
                      {table.getState().pagination.pageIndex + 1} {TextProps.ofText} {table.getPageCount()}
                    </strong>{' '}
                  </span>
                  <select
                    className="-pageJump"
                    value={table.getState().pagination.pageSize}
                    onChange={(e) => {
                      const newPageSize = Number(e.target.value);
                      if (manual && onPageSizeChange) {
                        onPageSizeChange(newPageSize);
                      } else {
                        table.setPageSize(newPageSize);
                      }
                    }}
                  >
                    {[10, 20, 30, 40, 50].map((optionPageSize) => (
                      <option key={optionPageSize} value={optionPageSize}>
                        {optionPageSize} {TextProps.rowsText}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="-next">
                  <Tooltip
                    text="Página siguiente"
                    placement="top"
                    disabled={!table.getCanNextPage()}
                  >
                    <button
                      className="-btn"
                      onClick={() => table.nextPage()}
                      disabled={!table.getCanNextPage()}
                    >
                      {TextProps.nextText}
                    </button>
                  </Tooltip>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

Table.propTypes = {
  className: string,
  columns: array.isRequired,
  filterable: bool,
  defaultPageSize: number,
  data: array.isRequired,
  keyField: string,
  resizable: bool,
  getTableRef: func,
  onPageSizeChange: func,
  defaultSortingMethod: func,
  handleToggleAllSelection: func,
  handleToggleSingleSelection: func,
  handleSetSelectAll: func,
  getRowProps: func,
  getRowId: func,
  selectInputComponent: func,
  selectAll: bool,
  isSelected: func,
  manual: bool,
  pageSize: number,
  onFilterChange: func,
  onSortedChange: func,
  loading: bool,
  loadingContent: node,
  pages: number,
  page: number,
  expandable: bool,
  subComponent: func,
  resetColumnFilters: bool,
  isChildTable: bool,
  hidePagination: bool,
  hideFooter: bool
};

export default Table;
